import capitalize from 'capitalize';
import cx from 'classnames';
import React from 'react';
import { toast } from 'sonner';
import { EVoicemailTouchBehavior } from 'types/touch-trigger';
import { ETouchType } from 'types/touch-type';

import {
  ButtonBase, Dialog, Tab,
  Tabs,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EmailIcon from '@material-ui/icons/Email';
import PermPhoneMsgIcon from '@material-ui/icons/PermPhoneMsg';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import SettingsIcon from '@material-ui/icons/Settings';
import TextFormatIcon from '@material-ui/icons/TextFormat';

import ActionModal from 'components/ActionModal/ActionModal';
import ChooseExtraTouchContent from 'components/CampaignSteps/CustomTouches/ChooseExtraTouchContent';
import ChooseVoicemail from 'components/CampaignSteps/CustomTouches/ChooseVoicemail';
import EditBodyText from 'components/CampaignSteps/CustomTouches/EditBodyText';
import ReplaceDesign from 'components/CampaignSteps/CustomTouches/ReplaceDesign';
import VoicemailTouchBehaviorForm from 'components/CampaignSteps/CustomTouches/VoicemailTouchBehaviorForm';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import SimpleModalHeader from 'components/SimpleModalHeader/SimpleModalHeader';

import { resetCustomTouchTemplate } from 'utils/api';
import { isExtraCampaignTouch, showAPIErrorAlert } from 'utils/lib';

enum ETabType {
  ChooseContentTab = 'CHOOSE_CONTENT',
  ChooseVoicemailBehaviorTab = 'CHOOSE_VOICEMAIL_BEHAVIOR',
  ChooseVoicemailTab = 'CHOOSE_VOICEMAIL',
  EditTextTab = 'EDIT_TEXT',
  ReplaceDesignTab = 'REPLACE_DESIGN',
}

const useStyles = makeStyles((theme) => ({
  tabs: {
    margin: '40px 0 0',
  },
  tabsHidden: {
    display: 'none',
  },
  instructions: {
    color: theme.palette.darkGray.main,
    fontFamily: 'Montserrat',
    fontWeight: 500,
    margin: '30px 0 20px',
    fontSize: '18px',

    '& > span': {
      color: theme.palette.lightGray.main,
      fontSize: '15px',
    },
  },
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
}));

const getInitialEditTab = (touchTrigger: any) => {
  if (isExtraCampaignTouch(touchTrigger) && touchTrigger.requiresCustomTouchTemplate && !touchTrigger.customTouchTemplate) {
    return ETabType.ChooseContentTab;
  }

  const touchTypeName = touchTrigger.touchType.name;
  if (touchTypeName === ETouchType.Letter || touchTypeName === ETouchType.Email) return ETabType.EditTextTab;
  if (touchTypeName === ETouchType.Postcard) return ETabType.ReplaceDesignTab;
  if (touchTypeName === ETouchType.Voicemail) return ETabType.ChooseVoicemailBehaviorTab;
  return '';
};

interface Props {
  eventTypeVoicemails: any;
  touchTrigger: any;
  campaignTouchTriggers: any[];
  onReceiveEditedTouchTrigger: any;
  onClose: any;
}

const EditCustomTouchTemplateModal = ({
  eventTypeVoicemails, touchTrigger, campaignTouchTriggers, onReceiveEditedTouchTrigger, onClose,
}: Props) => {
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;

  const [currentTouchTrigger, setCurrentTouchTrigger] = React.useState(touchTrigger);
  const { touchType: { name: touchTypeName } } = currentTouchTrigger;
  const allowReset = !isExtraCampaignTouch(currentTouchTrigger);

  // Letters have both options, postcards only PDF, and emails only editing text
  const [activeTab, setActiveTab] = React.useState(getInitialEditTab(currentTouchTrigger));
  const [isConfirmingReset, setIsConfirmingReset] = React.useState(false);

  React.useEffect(() => {
    setCurrentTouchTrigger(touchTrigger);
    setActiveTab(getInitialEditTab(touchTrigger));
  }, [touchTrigger]);

  const onReceiveUpdatedTouchTrigger = (updatedTouchTrigger: any) => {
    setCurrentTouchTrigger(updatedTouchTrigger);
    onReceiveEditedTouchTrigger(updatedTouchTrigger);
  };

  const onResetCustomTouchTemplate = async () => {
    // Show a loading indicator during the upload
    showLoadingIndicatorModal();

    try {
      const { touchTrigger: updatedTouchTrigger } = await resetCustomTouchTemplate(currentTouchTrigger._id);

      // Callback to update the touch trigger locally
      onReceiveUpdatedTouchTrigger(updatedTouchTrigger);

      onClose();

      toast.success(`Your edits have been reset for this touch. The default campaign ${touchTypeName.toLowerCase()} will be used when sending out your touch.`);
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    }

    hideLoadingIndicatorModal();
  };

  return (
    <>
      {allowReset && isConfirmingReset && (
        <ActionModal
          backgroundColor="#ffffff"
          textColor="#1f2937"
          onClose={() => setIsConfirmingReset(false)}
          title="Reset Edits"
          message="Are you sure you want to reset your edits for this touch? The default materials will be used for sending out this touch."
          buttons={[
            <ButtonBase onClick={() => setIsConfirmingReset(false)} className={classes.cancelButton}>No</ButtonBase>,
            <ButtonBase onClick={onResetCustomTouchTemplate}>Yes</ButtonBase>,
          ]}
        />
      )}

      <Dialog
        open
        scroll="body"
        keepMounted
        maxWidth="lg"
        onClose={onClose}
      >
        {/* Use "Customize Your Touch" for VMs since technically it can be an email instead */}
        <SimpleModalHeader onClose={onClose} title={`Customize Your ${touchTypeName !== ETouchType.Voicemail ? capitalize(touchTypeName) : 'Touch'}`} />

        {activeTab !== ETabType.ChooseContentTab && (
          <Tabs
            className={cx(classes.tabs, touchTypeName !== ETouchType.Voicemail && touchTypeName !== ETouchType.Letter && classes.tabsHidden)}
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            indicatorColor="secondary"
            textColor="secondary"
            centered
          >
            {touchTypeName === ETouchType.Voicemail && (
              <Tab
                icon={<SettingsIcon />}
                label="Delivery"
                value={ETabType.ChooseVoicemailBehaviorTab}
              />
            )}

            {touchTypeName === ETouchType.Voicemail && currentTouchTrigger.metadata.voicemailTouchBehavior !== EVoicemailTouchBehavior.None && (
              <Tab
                icon={<PermPhoneMsgIcon />}
                label="Choose Recording"
                value={ETabType.ChooseVoicemailTab}
              />
            )}

            {touchTypeName === ETouchType.Voicemail && currentTouchTrigger.metadata.voicemailTouchBehavior !== EVoicemailTouchBehavior.AllLeads && (
              <Tab
                icon={<EmailIcon />}
                label="Edit Email"
                value={ETabType.EditTextTab}
              />
            )}

            {[ETouchType.Email, ETouchType.Letter].includes(touchTypeName) && (
              <Tab
                icon={<TextFormatIcon />}
                label="Edit Text"
                value={ETabType.EditTextTab}
              />
            )}

            {[ETouchType.Letter, ETouchType.Postcard].includes(touchTypeName) && (
              <Tab
                icon={<PictureAsPdfIcon />}
                label="Replace Design"
                value={ETabType.ReplaceDesignTab}
              />
            )}
          </Tabs>
        )}

        {activeTab === ETabType.ChooseContentTab && (
          <ChooseExtraTouchContent
            touchTrigger={currentTouchTrigger}
            campaignTouchTriggers={campaignTouchTriggers}
            onReceiveEditedTouchTrigger={(updatedTouchTrigger) => {
              onReceiveUpdatedTouchTrigger(updatedTouchTrigger);
              onClose();
            }}
            onCreateNewContent={() => setActiveTab(getInitialEditTab({
              ...currentTouchTrigger,
              requiresCustomTouchTemplate: false,
            }))}
          />
        )}

        {activeTab === ETabType.ChooseVoicemailBehaviorTab && (
          <VoicemailTouchBehaviorForm
            touchTrigger={currentTouchTrigger}
            onReceiveEditedTouchTrigger={onReceiveUpdatedTouchTrigger}
          />
        )}

        {activeTab === ETabType.ChooseVoicemailTab && (
          <ChooseVoicemail
            touchTrigger={currentTouchTrigger}
            onReceiveEditedTouchTrigger={onReceiveUpdatedTouchTrigger}
            eventTypeVoicemails={eventTypeVoicemails}
          />
        )}

        {activeTab === ETabType.EditTextTab && (
          <EditBodyText
            onClose={onClose}
            touchTrigger={currentTouchTrigger}
            onReceiveEditedTouchTrigger={onReceiveUpdatedTouchTrigger}
            setIsConfirmingReset={setIsConfirmingReset}
            allowReset={allowReset}
          />
        )}

        {activeTab === ETabType.ReplaceDesignTab && (
          <ReplaceDesign
            onClose={onClose}
            touchTrigger={currentTouchTrigger}
            onReceiveEditedTouchTrigger={onReceiveUpdatedTouchTrigger}
            setIsConfirmingReset={setIsConfirmingReset}
            allowReset={allowReset}
          />
        )}

      </Dialog>
    </>
  );
};

export default EditCustomTouchTemplateModal;
